import React from 'react'
import { MenuItem, TextField, Typography, styled } from '@mui/material'
import { IOlexFileLine, IOlexMovableLine } from 'src/generated-types/olex-match'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { ListRow, PanelCard, PanelHeader, SectionLabel } from './ExpandGrid.style'
import { CountChip } from './PublishMooring.style'
import { OlexMapHighlight, lineHighlight, previewHighlight } from './olexMatch.action'

interface OlexManualMovesProps {
  lines: IOlexMovableLine[]
  fileLines: IOlexFileLine[]
  onMove: (lineId: number, fileLine?: number) => void
  onHighlight: (highlight?: OlexMapHighlight) => void
  t: (key: string, options?: object) => string
}

// A row per line the upload would remove: leave it removed, or move it to a new line of the file to keep it.
const OlexManualMoves: React.FC<OlexManualMovesProps> = ({ lines, fileLines, onMove, onHighlight, t }) => {
  const taken = new Set(lines.filter(line => line.fileLine != null).map(line => line.fileLine))
  const fileLineByIndex = new Map(fileLines.map(line => [line.index, line]))
  const clearHighlight = () => onHighlight(undefined)

  // The name the line has after the upload; the file's own name only where it differs.
  const destination = (line: IOlexFileLine) => (
    <>
      {t('revision-olex-manual-to', { name: line.name, corner: line.corner })}
      {line.olexName && line.olexName !== line.name && (
        <Note>{t('revision-olex-file-name', { name: line.olexName })}</Note>
      )}
    </>
  )

  return (
    <PanelCard>
      <PanelHeader>
        <SectionLabel>{t('revision-olex-manual-title')}</SectionLabel>
        <CountChip>{lines.length}</CountChip>
      </PanelHeader>
      <Typography fontSize={12} color="text.secondary">
        {t('revision-olex-manual-hint')}
      </Typography>
      {lines.map(line => (
        <ListRow key={line.id} onMouseEnter={() => onHighlight(lineHighlight(line.id))} onMouseLeave={clearHighlight}>
          <Typography fontSize={13}>
            <b>{line.name}</b>
            {line.corner && <Note>{t('revision-olex-at-corner', { corner: line.corner })}</Note>}
          </Typography>
          <TextField
            select
            size="small"
            value={line.fileLine ?? ''}
            onChange={event => onMove(line.id, event.target.value === '' ? undefined : Number(event.target.value))}
            SelectProps={{
              displayEmpty: true,
              onClose: clearHighlight,
              renderValue: value => {
                const target = value === '' ? undefined : fileLineByIndex.get(Number(value))
                return target ? destination(target) : t('revision-olex-remove')
              },
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="" onMouseEnter={() => onHighlight(lineHighlight(line.id))}>
              {t('revision-olex-remove')}
            </MenuItem>
            {fileLines
              .filter(target => !taken.has(target.index) || target.index === line.fileLine)
              .map(target => (
                <MenuItem
                  key={target.index}
                  value={target.index}
                  onMouseEnter={() => onHighlight(previewHighlight(target.positionId))}
                >
                  {destination(target)}
                </MenuItem>
              ))}
          </TextField>
        </ListRow>
      ))}
    </PanelCard>
  )
}

export default OlexManualMoves

const Note = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(0.75),
  fontSize: 11.5,
  fontWeight: 400,
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
}))
